import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Switch, Empty, Button, Select } from 'antd';
import {
  createTableChangeHandler,
  createScrollSync,
} from '@client/utils/table/tableUtils';
import {
  createExpandedRowRender,
  createTableColumns,
  createExtractionTableColumns,
} from '@client/feature/treatment/config/tableConfig';
import useIriisLinks from '@client/hooks/useIriisLinks';
import RiskSymbolsLegends from '@client/components/ui/RiskSymbolsLegends';
import TreatmentLegends from '@client/components/ui/TreatmentLegends';
import RiskIndexFootnotes from '@client/components/ui/RiskIndexFootnotes';
import CardView from './CardView';
import { encodeSearchCriteria } from '@client/feature/treatment/utils/searchCriteriaUrl';
import thumbnail from '../../../../../../img/thumbnail.svg';
import table from '../../../../../../img/table.svg';

const ResultItems = ({
  data,
  onSortChange,
  onPresetSortChange,
  currentSorting,
  searchData,
  filters,
  searchMode = 'rapide',
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState('table');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [exactMatch, setExactMatch] = useState(true);
  const [hideSelectionTags, setHideSelectionTags] = useState(false);
  const [showRiskStyle, setShowRiskStyle] = useState(() => localStorage.getItem('sage-show-bordered-table') !== 'false');

  const handleShowRiskStyleChange = (checked) => {
    setShowRiskStyle(checked);
    localStorage.setItem('sage-show-bordered-table', String(checked));
  };
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const tableWrapperRef = useRef(null);
  const tableHeaderRef = useRef(null);

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const sortOptions = [
    {
      value: 'risk_asc',
      label: t('treatment-search|Ordre croissant selon les indices de risque globaux'),
    },
    {
      value: 'risk_desc',
      label: t(
        'treatment-search|Ordre décroissant selon les indices de risque globaux'
      ),
    },
  ];

  /**
   * Legend section component
   */
  const LegendSection = () => (
    <div className="rounded-2xl bg-gray-50 border border-gray-200 px-4 sm:px-6 py-4 flex flex-col justify-center w-full lg:w-auto">
      <p className="font-bold text-[14px] sm:text-[16px] text-black mb-[16px]">
        {t('common|Légende')} :
      </p>
      <div className="space-y-4">
        <TreatmentLegends />
        <RiskSymbolsLegends title={t('treatment-search|Symboles de risque :')} />
      </div>
    </div>
  );

  const results = data?.results || [];

  // Collect enemy IDs from current page results for IRIIS links
  const enemyIds = useMemo(() => {
    const ids = new Set();
    results.forEach((record) => {
      record.treatment_enemies?.forEach((te) => {
        if (te?.enemy?.id) ids.add(te.enemy.id);
      });
    });
    // Also include enemy IDs from multichoix selection labels
    const selectedEnemies =
      searchData?.selectionLabels?.['treatment_enemies.enemy'] || [];
    selectedEnemies.forEach((e) => {
      if (e.value) ids.add(e.value);
    });
    return [...ids];
  }, [results, searchData?.selectionLabels]);

  const { data: iriisLinks } = useIriisLinks(enemyIds);

  const filteredResults = useMemo(
    () =>
      showSelectedOnly
        ? results.filter((item) => selectedRowKeys.includes(item.id))
        : results,
    [showSelectedOnly, results, selectedRowKeys]
  );

  const isExtraction = searchMode === 'extraction';

  // Encode search criteria for detail page URLs
  const encodedCriteria = useMemo(
    () => encodeSearchCriteria(searchData?.selectionLabels),
    [searchData?.selectionLabels]
  );

  // Build URL query suffix for detail page links (includes search criteria for sharing)
  const detailQuerySuffix = useMemo(() => {
    const parts = [];
    if (filters?.cultures?.length) parts.push(`cultures=${filters.cultures.join(',')}`);
    if (encodedCriteria) parts.push(`sc=${encodedCriteria}`);
    return parts.length > 0 ? `&${parts.join('&')}` : '';
  }, [filters?.cultures, encodedCriteria]);

  const columns = useMemo(
    () =>
      isExtraction
        ? createExtractionTableColumns(t, searchData, filters, iriisLinks, detailQuerySuffix)
        : createTableColumns(
            t,
            currentSorting,
            searchData,
            filters,
            searchMode,
            iriisLinks,
            detailQuerySuffix
          ),
    [t, currentSorting, searchData, filters, searchMode, isExtraction, iriisLinks, detailQuerySuffix]
  );

  const handleTableChange = useMemo(
    () => createTableChangeHandler(null, onSortChange, columns),
    [onSortChange, columns]
  );

  const expandedRowRender = useMemo(() => createExpandedRowRender(), []);

  useEffect(() => {
    if (!filters.sort) {
      onPresetSortChange && onPresetSortChange('risk_asc');
    }
  }, []); // Only on mount

  useEffect(() => {
    const tableHeader = tableHeaderRef.current;
    const tableBody = document.querySelector('.ant-table-content');
    return createScrollSync(tableHeader, tableBody);
  }, [data?.page, filteredResults]);

  useEffect(() => {
    if (hideSelectionTags) {
      setExpandedRowKeys([]);
    } else {
      const currentPageKeys = filteredResults
        .filter((item) =>
          item.treatment_cultures?.some(
            (tc) => tc.culture || tc.enemies?.length
          )
        )
        .map((item) => item.id);
      setExpandedRowKeys(currentPageKeys);
    }
  }, [filteredResults, hideSelectionTags]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
    }),
    [selectedRowKeys]
  );

  if (!results.length) {
    return (
      <div className="text-center py-8">
        <Empty
          description={t('common|No results found')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Action buttons and Legend section */}
      <div className="flex flex-col lg:flex-row justify-between my-[32px] gap-[16px]">
        {/* Left side - Action buttons */}
        <div className="flex flex-col gap-[16px] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[16px] px-4 sm:px-6 py-4 w-full lg:w-auto">
          <p className="font-medium text-[14px] sm:text-[16px]">
            {t("treatment-search|Boutons d'actions sur le tableau")} :
          </p>

          {/* View toggle buttons (hidden in extraction mode — table only) */}
          {!isExtraction && (
            <div className="flex flex-col sm:flex-row gap-2 border-b border-grey-200 pb-[16px]">
              <Button
                type={view === 'table' ? 'primary' : 'default'}
                size="large"
                onClick={() => handleViewChange('table')}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <img src={table} alt="table" className="flex-shrink-0" />
                <span className="truncate">
                  {t('common|Affichage tableau')}
                </span>
              </Button>
              <Button
                type={view === 'card' ? 'primary' : 'default'}
                size="large"
                onClick={() => handleViewChange('card')}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <img
                  src={thumbnail}
                  alt="thumbnail"
                  className="flex-shrink-0"
                />
                <span className="truncate">
                  {t('common|Affichage vignettes')}
                </span>
              </Button>
            </div>
          )}

          {/* Switch options */}
          <div className="flex gap-[8px] items-start sm:items-center">
            <Switch
              checked={showSelectedOnly}
              onChange={setShowSelectedOnly}
              disabled={selectedRowKeys.length === 0}
              className="flex-shrink-0 mt-1 sm:mt-0"
            />
            <span className="text-[13px] sm:text-[14px] leading-tight">
              {t('common|Afficher uniquement les cases sélectionnées')}
            </span>
          </div>
          <div className="flex gap-[8px] items-start sm:items-center">
            <Switch
              checked={exactMatch}
              onChange={setExactMatch}
              className="flex-shrink-0 mt-1 sm:mt-0"
            />
            <span className="text-[13px] sm:text-[14px] leading-tight">
              {t('common|Résultats avec correspondance parfaite')}
            </span>
          </div>
          <div className="flex gap-[8px] items-start sm:items-center">
            <Switch
              checked={hideSelectionTags}
              onChange={setHideSelectionTags}
              className="flex-shrink-0 mt-1 sm:mt-0"
            />
            <span className="text-[13px] sm:text-[14px] leading-tight">
              {t('common|Masquer les étiquettes de sélection')}
            </span>
          </div>
          <div className="flex gap-[8px] items-start sm:items-center">
            <Switch
              checked={showRiskStyle}
              onChange={handleShowRiskStyleChange}
              className="flex-shrink-0 mt-1 sm:mt-0"
            />
            <span className="text-[13px] sm:text-[14px] leading-tight">
              {t('common|Afficher le tableau avec bordures')}
            </span>
          </div>

          {/* Sort dropdown */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center mt-2 pt-4 border-t border-gray-200">
            <label className="whitespace-nowrap text-[13px] sm:text-[14px]">
              {t('common|Trier par')} :
            </label>
            <Select
              value={filters.sort || 'risk_asc'}
              className="w-full sm:w-[380px]"
              options={sortOptions}
              onChange={(value) => onPresetSortChange && onPresetSortChange(value)}
            />
          </div>
        </div>

        {/* Right side - Legend */}
        <LegendSection />
      </div>

      <RiskIndexFootnotes>
      {/* Render Card View (not available in extraction mode) */}
      {!isExtraction && view === 'card' && (
        <CardView
          data={data}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          showSelectedOnly={showSelectedOnly}
          iriisLinks={iriisLinks}
          searchMode={searchMode}
          detailQuerySuffix={detailQuerySuffix}
        />
      )}

      {/* Render Table View */}
      {(isExtraction || view === 'table') && (
        <Table
          className={!showRiskStyle ? 'risk-results-table' : ''}
          columns={columns}
          dataSource={filteredResults}
          rowKey="id"
          bordered
          size="small"
          rowSelection={rowSelection}
          pagination={false}
          onChange={handleTableChange}
          loading={!data}
          scroll={{ x: 'max-content' }}
          expandable={
            isExtraction
              ? undefined
              : {
                  expandedRowRender,
                  expandedRowKeys,
                  onExpand: (expanded, record) => {
                    setExpandedRowKeys((prev) =>
                      expanded
                        ? [...prev, record.id]
                        : prev.filter((key) => key !== record.id)
                    );
                  },
                  expandIcon: () => null,
                  expandIconColumnIndex: -1,
                }
          }
        />
      )}
      </RiskIndexFootnotes>
    </div>
  );
};

export default ResultItems;
